import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { addNotification } from "store/slices/uiSlice.js";

import { getTheme } from "Theme";
import { DateTime } from "luxon";
import { faBell, faCog } from "@fortawesome/free-solid-svg-icons";

import { Clickable, IconButton } from "./index.js";
import { useHistory } from "react-router-dom";
import Button from "./Button";
import { errorHandler } from "globalResources/util.js";
const theme = getTheme();

/**
 * Styled Components
 */
const Main = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
`;

/**
 * Special div used to overlay the notification icon over the main menu notifications quick view button
 */
const MainMenuIconContainer = styled.div`
  display: flex;
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 38px;
  right: 50px;
  background: ${theme.PAGE_BACKGROUND};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  width: 400px;
  border-radius: 10px;
`;

const NotificationsLayoutHeader = styled.div`
  height: 40px;
  color: white;
  background: ${theme.MAIN_COLOR};
  font-weight: bold;
  font-size: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DropdownHeaderText = styled.div`
  cursor: pointer;
`;

const NotificationsLayoutFooter = styled.div`
  height: 30px;
  display: flex;
  color: white;
`;

/**
 * Icon for the Notification Header modified to display the total number of unread notifications.
 */
const NotificationHeaderIcon = styled.div`
  border-radius: 50%;
  height: 24px;
  justify-self: center;
  background-color: rgba(125, 255, 125, 1);
  aspect-ratio: 1;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: black;
  margin-left: 1%;
`;

const NavigationBarIndicatorDot = styled.div`
  width: 10px;
  height: 10px;
  opacity: ${(props) => (props.showDot > 0 ? 1 : 0)};
  background-color: rgba(125, 255, 125, 1);
  border-radius: 50%;
  position: absolute;
  top: 8px;
  right: 44px;
`;

const SeeAllButton = styled(Button)`
  cursor: pointer;
  border-radius: 10px;
  background-color: ${theme.MAIN_COLOR};
  color: white;
  font-weight: bold;
  font-size: 10px;
  width: 140px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
`;

/**
 * Container used to hold all notifications so that the scroll bar will leave the Notifications controls visible.
 */
const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 50vh;
  overflow: auto;
`;

/**
 * Formatted div used to show each individual notification in the NotificationList
 */
const Notification = styled.div`
  width: 96%;
  margin-left: 2%;
  margin-right: 2%;
  display: grid;
  grid-template-columns: 9fr 1fr;
  grid-template-rows: 1fr auto auto;
  grid-template-areas:
    "title icon"
    "description time";
  cursor: pointer;
  padding: 5px;
  margin: 4px;
  border-bottom: 1px solid ${theme.MAIN_COLOR};
  &:hover {
    background-color: ${theme.SECONDARY_COLOR};
  }
`;

const Subject = styled.div`
  grid-area: title;
  font-weight: bold;
`;

const NoNotificationsMessage = styled.div`
  text-align: center;
  font-size: 14px;
  margin: 10px;
  color: ${theme.INPUT_TEXT};
`;

const Message = styled.div`
  grid-area: description;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12px;
`;

const CreatedAt = styled.div`
  grid-area: time;
  text-align: center;
  align-self: end;
  font-size: 10px;
`;

/**
 * Javascript
 */

/**
 * Helper function to determine how much has time has passed since a notification was created.
 *
 * @param {String to get past time for} timeString
 * @returns
 */
const getPastTime = (timeString) => {
  const now = DateTime.now();
  const notificationTime = DateTime.fromISO(timeString);
  const timePast = now.diff(notificationTime, [
    "years",
    "months",
    "days",
    "hours",
    "minutes",
    "seconds",
  ]);

  var pastTimeString;
  if (timePast.years !== 0) {
    pastTimeString = timePast.years + " years";
  } else if (timePast.months !== 0) {
    pastTimeString = Math.round(timePast.months) + " months";
  } else if (timePast.days !== 0) {
    pastTimeString = Math.round(timePast.days) + "D";
  } else if (timePast.hours !== 0) {
    pastTimeString = Math.round(timePast.hours) + "H";
  } else if (timePast.minutes !== 0) {
    pastTimeString = Math.round(timePast.minutes) + "M";
  } else {
    pastTimeString = Math.round(timePast.seconds) + "S";
  }
  return pastTimeString;
};

const NotificationsDropdown = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const notificationsRef = useRef(null);

  const [notificationsCnt, setNotificationsCnt] = useState(0);

  const [showNotificationsDropdown, setShowNotificationsDropdown] =
    useState(false);

  const [notifications, setNotifications] = useState([
    {
      subject: "null",
      message: "null",
      createdAt: "2026-07-01T00:00:00.000Z",
      read: "null",
    },
  ]);

  const fetchNotifications = async () => {
    try {
      let res = await axios.get("/notifications/simple");
      setNotifications(res.data.notifications);
      setNotificationsCnt(res.data.total);
    } catch (err) {
      const { message } = errorHandler(err);
      dispatch(addNotification({type: "error", message }));
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []); 

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setTimeout(() => setShowNotificationsDropdown(false), 100);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationsRef]);

  useEffect(() => {
    const intervalId = setInterval(fetchNotifications, 10000);
    return () => clearInterval(intervalId);
  }, []); 

  const handleNotificationClick = async (notification) => {
    axios.patch("/notification/read", {
      data: {
        notificationId: notification._id,
      },
    });
    fetchNotifications();
    setShowNotificationsDropdown(false);
    history.push({
      pathname: `${notification.link}`,
      state: {},
    });
  };

  return (
    <Main>
      <MainMenuIconContainer>
        <Clickable style={{ marginLeft: "auto" }}>
          <IconButton
            icon={faBell}
            onClick={() => {
              setShowNotificationsDropdown(true);
              fetchNotifications();
            }}
          />
        </Clickable>
        <NavigationBarIndicatorDot showDot={notificationsCnt} />
      </MainMenuIconContainer>
      {showNotificationsDropdown && (
        <DropdownContainer ref={notificationsRef}>
          <NotificationsLayoutHeader
            style={{ textAlign: "center", color: "white" }}
          >
            <NotificationHeaderIcon>
              {notificationsCnt >= 100 ? `99+` : notificationsCnt}
            </NotificationHeaderIcon>
            <DropdownHeaderText>Unread Notifications</DropdownHeaderText>
            <IconButton icon={faCog} style={{ justifySelf: "right" }} />
          </NotificationsLayoutHeader>
          {notificationsCnt === 0 ? (
            <NoNotificationsMessage>
              No unread notifications
            </NoNotificationsMessage>
          ) : (
            <NotificationsList>
              {notifications.map((notification, index) => {
                return (
                  <Notification
                    onClick={() => {
                      handleNotificationClick(notification);
                    }}
                    key={index}
                  >
                    <Subject read={notification.read}>
                      {notification.subject}
                    </Subject>
                    <Message>{notification.message}</Message>
                    <CreatedAt>{getPastTime(notification.createdAt)}</CreatedAt>
                  </Notification>
                );
              })}
            </NotificationsList>
          )}
          <NotificationsLayoutFooter>
            <SeeAllButton
              onClick={() => {
                setShowNotificationsDropdown(false);
                history.push("/notifications");
              }}
            >
              See All Notifications
            </SeeAllButton>
          </NotificationsLayoutFooter>
        </DropdownContainer>
      )}
    </Main>
  );
};
export default NotificationsDropdown;
